import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Search as SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import PostCard from '../components/blog/PostCard';
import { Container } from '../components/ui/Container';
import Pagination from '../components/ui/Pagination';
import Spinner from '../components/ui/Spinner';
import { usePagination } from '../hooks/usePagination';
import SEO from '../components/seo/SEO';
import { searchArticles } from '../store/actions/searchActions';
import { transformArticles } from '../utils/transformData';
import { trackSearch } from '../utils/analytics';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const SearchHeader = styled.div`
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SearchTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
`;

const SearchQuery = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
  }
  
  strong {
    color: ${props => props.theme.colors.foreground};
    font-weight: 600;
  }
`;

const ResultsCount = styled.p`
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-top: 0.5rem;
  
  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 4rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }
  
  @media (min-width: 768px) {
    gap: 2rem;
    margin-bottom: 5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    margin-bottom: 6rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.5rem;
  color: ${props => props.theme.colors.mutedForeground};
  opacity: 0.5;
  
  @media (min-width: 1024px) {
    width: 5rem;
    height: 5rem;
    margin-bottom: 2rem;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const EmptyText = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
  }
`;

const Search = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language, apiRegion } = useLanguage();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  // Use language from context (set based on region from URL)
  const activeLanguage = language;
  
  const { searchResults: searchResultsFromStore, loading, error } = useSelector(state => state.search);
  
  useEffect(() => {
    if (query.trim()) {
      dispatch(searchArticles(query.trim(), { 
        page: 1, 
        limit: 50,
        lang: activeLanguage,
        region: apiRegion
      }));
    }
  }, [dispatch, query, activeLanguage, apiRegion]);

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    // The reducer stores articles in searchResults.articles
    const articles = searchResultsFromStore?.articles || [];
    return transformArticles(articles);
  }, [query, searchResultsFromStore]);

  useEffect(() => {
    if (query.trim() && !loading && !error) {
      trackSearch({ query: query.trim(), resultCount: searchResults.length });
    }
  }, [query, searchResults.length, loading, error]);

  const { paginatedItems, currentPage, totalPages, goToPage } = usePagination(searchResults, 6);

  const searchUrl = activeLanguage === 'en' 
    ? `/search${query ? `?q=${encodeURIComponent(query)}` : ''}`
    : `/${activeLanguage}/search${query ? `?q=${encodeURIComponent(query)}` : ''}`;

  return (
    <PageWrapper>
      <SEO
        title={query ? `Search: ${query}` : 'Search'}
        description={query ? `Search results for "${query}"` : 'Search for articles on Bloomwik'}
        url={searchUrl}
        language={activeLanguage}
        robots="noindex, follow"
      />
      <Header />
      <Main>
        <Container>
          <SearchHeader>
            <SearchTitle>Search Results</SearchTitle>
            {query && (
              <>
                <SearchQuery>
                  Results for: <strong>{query}</strong>
                </SearchQuery>
                <ResultsCount>
                  {t('search.resultsCount', { count: searchResults.length })}
                </ResultsCount>
              </>
            )}
          </SearchHeader>

          {!query ? (
            <EmptyState>
              <EmptyIcon>
                <SearchIcon />
              </EmptyIcon>
              <EmptyTitle>Start Searching</EmptyTitle>
              <EmptyText>
                Use the search bar in the header to find articles by title, content, category, or author.
              </EmptyText>
            </EmptyState>
          ) : loading ? (
            <Spinner />
          ) : error ? (
            <EmptyState>
              <EmptyIcon>
                <SearchIcon />
              </EmptyIcon>
              <EmptyTitle>Search Error</EmptyTitle>
              <EmptyText>
                An error occurred while searching. Please try again.
              </EmptyText>
            </EmptyState>
          ) : searchResults.length > 0 ? (
            <>
              <PostsGrid>
                {paginatedItems.map((post, index) => (
                  <PostCard
                    key={post._id || `${post.title}-${index}`}
                    {...post}
                    index={index}
                  />
                ))}
              </PostsGrid>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={goToPage}
                />
              )}
            </>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <SearchIcon />
              </EmptyIcon>
              <EmptyTitle>No Results Found</EmptyTitle>
              <EmptyText>
                We couldn't find any articles matching "<strong>{query}</strong>". Try different keywords or browse our categories.
              </EmptyText>
            </EmptyState>
          )}
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Search;

